import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowTopRightOnSquareIcon, 
  ChartBarIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useInView } from 'react-intersection-observer';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'streaming', label: 'Media Platforms' },
    { id: 'automation', label: 'Building Automation' },
    { id: 'collaboration', label: 'Enterprise Software' },
    { id: 'radiation', label: 'Safety Systems' },
    { id: 'security', label: 'Security Platforms' },
  ];

  const projects = [
    {
      id: 1,
      category: 'streaming',
      title: 'Inavada OTT Platform - Enterprise IPTV/OTT Solution',
      description: 'Ultra-compact infrastructure delivering 70-100 live TV channels from just 2U rack space to 5,000+ concurrent users',
      image: '/api/placeholder/400/300',
      technologies: ['Cross-Platform Apps', 'Adaptive Streaming', 'Satellite Processing', 'Multicast Tech', 'Dell OEM'],
      metrics: {
        improvement: '5,000 concurrent sessions',
        savings: '60-80% infrastructure cost',
        timeline: '18 months',
        uptime: '99.9% uptime'
      },
      client: 'Universities & Healthcare Facilities',
      testimonial: 'The platform revolutionized our TV distribution. Ultra-compact design with massive channel density.',
      highlights: [
        '50+ institutional installations',
        'Cross-platform ecosystem (iOS, Android, Web, Roku, Fire TV)',
        'Software-based solution eliminating set-top boxes',
        'Professional Dell OEM hardware with 4-hour support'
      ]
    },
    {
      id: 2,
      category: 'streaming',
      title: 'Tuner.info Local Broadcast Platform',
      description: 'Specialized multi-tenant streaming platform for master antenna services with AI-driven content delivery',
      image: '/api/placeholder/400/300',
      technologies: ['Multi-Tenant SaaS', 'Master Antenna Integration', 'AI Content Delivery', 'PMS Integration', 'Cloud CDN'],
      metrics: {
        improvement: 'Multi-tenant scalability',
        savings: '70-90% cable distribution cost',
        timeline: '24 months',
        performance: 'Sub-second latency'
      },
      client: 'Assisted Living & Student Housing',
      testimonial: 'Only streaming broadcaster specializing in master antenna services. Revolutionary approach.',
      highlights: [
        '200+ facility installations',
        'Wireless distribution eliminating cable outlets',
        'Senior-friendly Tuner Simple interface',
        '7-day catchup service with AI curation'
      ]
    },
    {
      id: 3,
      category: 'automation',
      title: 'Hydronic Automation HVAC Solution',
      description: 'AI-powered building automation achieving 35-45% energy reduction with ±0.5°C precision control',
      image: '/api/placeholder/400/300',
      technologies: ['IoT Sensors', 'Machine Learning', 'PID Controllers', 'Building Management', 'HomeAssistant'],
      metrics: {
        improvement: '35-45% energy reduction',
        savings: '25-30% utility cost decrease',
        timeline: '30 months',
        precision: '±0.5°C accuracy'
      },
      client: 'Commercial Buildings & Healthcare',
      testimonial: 'Energy savings exceeded all expectations. The machine learning optimization is incredible.',
      highlights: [
        '50+ commercial deployments',
        'Predictive maintenance reducing service calls by 60%',
        'Multi-zone independent climate control',
        'Integration with smart grid and renewable energy'
      ]
    },
    {
      id: 4,
      category: 'collaboration',
      title: 'Raydesk Collaboration System',
      description: 'Enterprise collaboration platform supporting 10,000+ concurrent users with AI-powered productivity features',
      image: '/api/placeholder/400/300',
      technologies: ['React/TypeScript', 'WebRTC', 'Microservices', 'Machine Learning', 'Global CDN'],
      metrics: {
        improvement: '40% meeting time reduction',
        savings: '60% faster project delivery',
        timeline: '36 months',
        performance: 'Sub-200ms global latency'
      },
      client: 'Technology & Financial Services',
      testimonial: 'The platform transformed our remote collaboration. AI-powered insights are game-changing.',
      highlights: [
        '100+ enterprise customers with 50,000+ active users',
        'Real-time document collaboration with simultaneous editing',
        'AI-powered meeting transcription and action items',
        'Zero-trust security with end-to-end encryption'
      ]
    },
    {
      id: 5,
      category: 'radiation',
      title: 'Nucleolenz Radiation Detection Systems',
      description: 'Advanced radiation monitoring with 0.1 µSv/h sensitivity and 99.5% isotope identification accuracy',
      image: '/api/placeholder/400/300',
      technologies: ['Spectroscopic Analysis', 'AI Analytics', 'Real-time Processing', 'Regulatory Compliance', 'IoT Networks'],
      metrics: {
        improvement: '0.1 µSv/h detection sensitivity',
        savings: '75% compliance cost reduction',
        timeline: '42 months',
        accuracy: '99.5% isotope identification'
      },
      client: 'Nuclear Facilities & Medical Institutions',
      testimonial: 'Industry-leading detection sensitivity with comprehensive regulatory compliance automation.',
      highlights: [
        '75+ installations across nuclear facilities',
        'Zero safety incidents with automated emergency response',
        'NRC and IAEA regulatory compliance automation',
        'Sub-second real-time processing for critical safety'
      ]
    },
    {
      id: 6,
      category: 'security',
      title: 'TotalSecure CMS Platform',
      description: 'High-security content management platform supporting 100,000+ concurrent users with zero security incidents',
      image: '/api/placeholder/400/300',
      technologies: ['Django Wagtail', 'React/Next.js', 'WAF Protection', 'DDoS Mitigation', 'Container Security'],
      metrics: {
        improvement: '100,000+ concurrent users',
        savings: '60% security incident reduction',
        timeline: '30 months',
        security: 'Zero security incidents'
      },
      client: 'Cybersecurity Organizations',
      testimonial: 'Perfect security record with exceptional performance. The platform exceeded all expectations.',
      highlights: [
        '40+ security organization websites',
        'SOC 2 Type II and ISO 27001 compliance',
        'Advanced threat protection with real-time monitoring',
        'Sub-100ms global response with edge optimization'
      ]
    },
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="portfolio" className="section-padding bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Portfolio & <span className="gradient-text">Case Studies</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Real projects, measurable results. Explore how I've helped businesses achieve 
              their technology goals with innovative solutions.
            </p>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Projects Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCategory}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  variants={itemVariants}
                  className="card overflow-hidden group"
                  whileHover={{ y: -10 }}
                  layout
                >
                  {/* Project Image */}
                  <div className="relative h-48 bg-gradient-to-br from-primary-500 to-secondary-500 overflow-hidden">
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center p-4">
                        <h3 className="text-lg font-bold mb-2">{project.title}</h3>
                        <p className="text-sm opacity-90">{project.client}</p>
                      </div>
                    </div>
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-primary-600/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <ArrowTopRightOnSquareIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  {/* Project Content */}
                  <div className="p-6">
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                      {project.description}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded text-xs"
                        >
                          {tech}
                        </span>
                      ))}
                      {project.technologies.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded text-xs">
                          +{project.technologies.length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="flex items-center">
                        <ChartBarIcon className="h-4 w-4 text-green-500 mr-2" />
                        <div className="text-xs">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {project.metrics.improvement}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 text-blue-500 mr-2" />
                        <div className="text-xs">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {project.metrics.savings}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Client Testimonial */}
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg mb-4">
                      <p className="text-xs text-gray-600 dark:text-gray-300 italic">
                        "{project.testimonial}"
                      </p>
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mt-1">
                        - {project.client}
                      </p>
                    </div>

                    {/* View Details Button */}
                    <motion.button
                      className="w-full bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors duration-200"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      View Case Study
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Portfolio Summary */}
          <motion.div
            variants={itemVariants}
            className="mt-16 grid md:grid-cols-4 gap-6 text-center"
          >
            {[
              { icon: TrophyIcon, number: '6', label: 'Major Platform Deployments', color: 'text-yellow-500' },
              { icon: CurrencyDollarIcon, number: '35-45%', label: 'Average Energy Savings', color: 'text-green-500' },
              { icon: ChartBarIcon, number: '99.9%', label: 'System Uptime Achieved', color: 'text-blue-500' },
              { icon: ClockIcon, number: '13+', label: 'Years Experience', color: 'text-purple-500' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="p-6 bg-gray-50 dark:bg-gray-800 rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <stat.icon className={`h-8 w-8 ${stat.color} mx-auto mb-3`} />
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            variants={itemVariants}
            className="mt-12 text-center bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-4">
              Ready to Create Your Success Story?
            </h3>
            <p className="text-lg mb-6 text-primary-100">
              Join major enterprises and institutions who have achieved industry-leading performance with transformative technology solutions.
            </p>
            <motion.button
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Start Your Project
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;